import React, {useCallback, useEffect, useRef, useState} from "react";
import { Collapse } from "react-bootstrap";
import {isActivitiesList, formatActivitiesList, activitiesListMatches} from "./VisiblePart";
import * as PropTypes from "prop-types";
import {NBSP} from "../../../lib/utils";
import _ from "lodash";

const CollapsiblePart = ({ listRef, list, onToggle = null, isOpen = false, markText = null, openOnMarked = false }) => {

  const isActivities = isActivitiesList(list);
  const formatList = (list, markText = null) => {
    if (isActivitiesList(list)) {
      return formatActivitiesList(list, markText);
    }
    return markText ? list.map(el => markText(el)) : list;
  };
  const scrollerRef = useRef();
  const [items, setItems] = useState(formatList(list, markText));
  const [hasMarkedItems, setHasMarkedItems] = useState(false);
  const [firstMarked, setFirstMarked] = useState(null);
  // PLEX-6628: which edges have more items past them, so the CSS can fade those edges.
  // Overlay scrollbars (Firefox, or a Mac set to show scroll bars only when scrolling)
  // otherwise give no sign that the list scrolls.
  const [overflow, setOverflow] = useState({above: false, below: false});
  const updateOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    const above = el.scrollTop > 1;
    const below = el.scrollHeight - el.scrollTop - el.clientHeight > 1;
    setOverflow(prev => (prev.above === above && prev.below === below ? prev : {above, below}));
  }, []);

  const hideToggle = (openOnMarked && hasMarkedItems) || !onToggle;

  useEffect(() => {
    setFirstMarked(scrollerRef.current?.querySelector('mark') || null);
  });

  useEffect(() => {
    if (hasMarkedItems && scrollerRef.current) {
      // window.scrollIntoView has too many unintended side effects, so set the scroll directly
      if (firstMarked != null) {
        scrollerRef.current.scrollTop = firstMarked.offsetTop - scrollerRef.current.offsetTop;
      }
    }
  });

  useEffect(() => {
    const unmarkedItems = formatList(list);
    if (markText) {
      const markedItems = formatList(list, markText);
      // PLEX-6697: activity elements always differ once they carry markText, so comparing them
      // opened every activity list under any filter; ask whether anything in the list matches
      // instead, evidence included.
      const hasMarked = isActivities
        ? activitiesListMatches(list, markText)
        : !_.isEqual(unmarkedItems, markedItems);
      setItems(hasMarked ? (_.isEqual(items, markedItems) ? items : markedItems) : unmarkedItems);
      setHasMarkedItems(hasMarked);
    }
    else {
      setItems(_.isEqual(items, unmarkedItems) ? items : unmarkedItems);
      setHasMarkedItems(false);
    }
  }, [list, markText]);

  // After every render: the items, the open state or the scroll to a marked item may have changed
  useEffect(updateOverflow);

  // A width change rewraps the items, which changes how far the list overflows
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(el);
    if (el.firstElementChild) {
      observer.observe(el.firstElementChild);
    }
    return () => observer.disconnect();
  }, [updateOverflow]);

  if (openOnMarked && !isOpen && hasMarkedItems) {
    isOpen = true;
  }

  return items.length && (
    <span ref={listRef} className="collapsible-collapsible">
      <span
        className={`top collapse-toggler ${isOpen ? "" : "collapsible-collapsed"}${hideToggle ? " hidden" : ""}`}
        onClick={onToggle}
        title={`${isOpen ? "collapse" : "show"} ${items.length} more results`}
      >
        &nbsp;{isOpen ? (<span className={`glyphicon glyphicon-triangle-bottom`} />) : (<span className={`glyphicon glyphicon-triangle-right`} />)}
      </span>
      <Collapse in={isOpen} onEntered={updateOverflow}>
        <div
          ref={scrollerRef}
          className={`collapsible-scroller${overflow.above ? " more-above" : ""}${overflow.below ? " more-below" : ""}`}
          onScroll={updateOverflow}
        >
          <ul className={`collapsed-list collapse-hidden-in${isActivities && " activities" || ""}`}>{
            items.map((item, item_idx) => (<li key={item_idx}>{item}</li>))
          }
          </ul>
        </div>
      </Collapse>
      <span
        className={`bottom collapse-toggler${isOpen ? "" : " collapsible-collapsed"}${!isOpen || hideToggle ? " hidden" : ""}`}
        onClick={onToggle}
        title={`${isOpen ? "collapse" : "show"} ${
          items.length
        } more results`}
      >
        {isOpen ? (<span className={`glyphicon glyphicon-triangle-top`} />) : (<span className={`glyphicon glyphicon-triangle-right`} />)}
      </span>
    </span>
  );
};

CollapsiblePart.propTypes = {
  list: PropTypes.array.isRequired,
  onToggle: PropTypes.func,
  isOpen: PropTypes.bool,
  markText: PropTypes.func,
};

export default CollapsiblePart;
